import {
  Text,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Primary13SemiBold,
  Primary13Regular,
  Skeleton,
  XYChart,
  IconCircleInfo,
  IconPeopleCircle,
  Table,
  Td,
  Tr,
  Box,
  Flex
} from '@sequencehq/core-components'
import { calculateYAxisLabels } from 'RevenueRecognition/view/utils/graphUtils'
import { Currency, useQuery } from '@sequencehq/api/utils'
import { ProgressBar } from 'RevenueRecognition/view/common/ProgressBar'
import { ChartTooltip } from 'RevenueRecognition/view/common/ChartTooltip'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import invariant from 'tiny-invariant'
import { add, format, formatISO } from '@sequencehq/utils/dates'
import { toCurrencySymbol, toMoney } from '@sequencehq/utils'
import { useMemo } from 'react'
import { calculateProgressPercentages } from '../../../common/progressPercentages'

/**
 * The tooltip displayed by the XYChart component when hovering over
 * different points in the chart.
 * @param date - the formatted date of the item
 * @param balances - the balances on the date
 * @param currency - the currency of the balances
 * @returns
 */
const GraphDataTooltip = ({
  date,
  userEmail,
  balances,
  displayDate,
  currency
}: {
  date: Date
  userEmail?: string
  displayDate?: string
  currency: Currency
  balances: {
    remaining: {
      absolute: number
      percentage: number
    }
    recognized: {
      absolute: number
      percentage: number
    }
  }
}) => {
  const tooltipDate = displayDate ? new Date(displayDate) : date

  return (
    <ChartTooltip width="344px">
      <ChartTooltip.Header>
        <Text {...Primary13SemiBold} color={GreyGrey70}>
          {format(tooltipDate, 'd MMM yyyy')}
        </Text>
      </ChartTooltip.Header>
      <ChartTooltip.Body padding="0 12px 4px">
        <Box width="100%">
          <Table variant="v2" width="100%" {...Primary13Regular}>
            <Tr borderBottom={`1px solid ${GreyGrey30}`}>
              <Td width="113px" color={GreyGrey80} padding={'8px 0'}>
                Recognized
              </Td>
              <Td color={GreyGrey60} padding={'8px 0'} textAlign="right">
                <Flex gap="8px" alignItems="center" justifyContent="flex-end">
                  {balances.recognized.percentage}%
                  <ProgressBar
                    width="56px"
                    percentage={balances.recognized.percentage}
                  />
                </Flex>
              </Td>
              <Td
                {...Primary13SemiBold}
                color={GreyGrey90}
                padding={'8px 0'}
                textAlign="right"
                width="74px"
              >
                {toMoney({
                  value: balances.recognized.absolute.toString(),
                  currency
                })}
              </Td>
            </Tr>
            <Tr borderBottom={userEmail ? `1px solid ${GreyGrey30}` : 'none'}>
              <Td color={GreyGrey80} padding={'8px 0'}>
                Remaining balance
              </Td>
              <Td color={GreyGrey60} padding={'8px 0'} textAlign="right">
                <Flex gap="8px" alignItems="center" justifyContent="flex-end">
                  {balances.remaining.percentage}%
                  <ProgressBar
                    width="56px"
                    percentage={balances.remaining.percentage}
                  />
                </Flex>
              </Td>
              <Td
                {...Primary13SemiBold}
                width="100%"
                color={GreyGrey90}
                padding={'8px 0'}
                textAlign="right"
              >
                {toMoney({
                  value: balances.remaining.absolute.toString(),
                  currency
                })}
              </Td>
            </Tr>
            {userEmail && (
              <Tr>
                <Td colSpan={3} padding={'8px 0'}>
                  <Flex gap="6px" alignItems="center">
                    <IconPeopleCircle color={GreyGrey70} />
                    <Box color={GreyGrey70} {...Primary13Regular}>
                      {userEmail}
                    </Box>
                  </Flex>
                </Td>
              </Tr>
            )}
          </Table>
        </Box>
      </ChartTooltip.Body>
    </ChartTooltip>
  )
}

type ChartDatum = {
  date: string
  value: number
  recognizedValue: number
  remainingValue: number
  displayDate?: string
  userEmail?: string
  balances: {
    recognized: {
      percentage: number
      absolute: number
    }
    remaining: {
      percentage: number
      absolute: number
    }
  }
}

const cloneDatum = (
  datum: ChartDatum,
  overrides: Partial<ChartDatum> = {}
): ChartDatum => ({
  ...datum,
  balances: {
    recognized: { ...datum.balances.recognized },
    remaining: { ...datum.balances.remaining }
  },
  ...overrides
})

interface ChartData {
  currency: Currency
  recognitionMethod: string
  dateFrom: Date
  dateTo: Date
  totalAmount: number
  rawPoints: ChartDatum[]
  recognitionTerm?: {
    start?: Date
    end?: Date
  }
  series: {
    cumulative: ChartDatum[]
    step: ChartDatum[]
    cumulativeStep: ChartDatum[]
  }
}

type UseDefRevScheduleProgressGraph = (props: { scheduleId: string }) =>
  | {
      chartData: ChartData
      isLoading: false
    }
  | {
      chartData: null
      isLoading: true
    }

/**
 * Data acquisition for the graph
 * @param scheduleId - the schedule id
 * @returns
 */
export const useDefRevScheduleProgressGraph: UseDefRevScheduleProgressGraph =
  props => {
    const scheduleQuery = useQuery(
      dashboardv99990101Client.getDeferredRevenueSchedule,
      { id: props.scheduleId },
      {
        select: res => {
          if (!res) {
            return null
          }

          const originalAmount = parseFloat(res.progress.original)

          const apiDateSeries = res.progress.details
            .map(detail => {
              const remainingAbsolute = parseFloat(detail.balance)
              const recognizedAbsolute = originalAmount - remainingAbsolute
              const balancesPercentages = calculateProgressPercentages({
                original: originalAmount,
                recognized: recognizedAbsolute,
                remaining: remainingAbsolute
              })

              return {
                date: detail.date,
                displayDate: detail.date,
                value: recognizedAbsolute,
                recognizedValue: recognizedAbsolute,
                remainingValue: remainingAbsolute,
                userEmail: detail.invokingUser?.name,
                balances: {
                  recognized: {
                    percentage: balancesPercentages.recognized,
                    absolute: recognizedAbsolute
                  },
                  remaining: {
                    percentage: balancesPercentages.remaining,
                    absolute: remainingAbsolute
                  }
                }
              }
            })
            .sort(
              (a, b) =>
                new Date(a.date).getTime() - new Date(b.date).getTime() ||
                a.value - b.value
            )
          const perDayCounts = apiDateSeries.reduce<Map<string, number>>(
            (acc, item) => {
              const parsedDate = new Date(item.date)
              const dayKey = !Number.isNaN(parsedDate.getTime())
                ? format(parsedDate, 'yyyy-MM-dd')
                : item.date.slice(0, 10)

              acc.set(dayKey, (acc.get(dayKey) ?? 0) + 1)

              return acc
            },
            new Map()
          )

          const perDayIndices = new Map<string, number>()

          const orderedSeries = apiDateSeries.reduce<ChartDatum[]>(
            (acc, item) => {
              const parsedDate = new Date(item.date)
              const dayKey = !Number.isNaN(parsedDate.getTime())
                ? format(parsedDate, 'yyyy-MM-dd')
                : item.date.slice(0, 10)

              const totalOnDay = perDayCounts.get(dayKey) ?? 1
              const currentIndex = (perDayIndices.get(dayKey) ?? 0) + 1
              perDayIndices.set(dayKey, currentIndex)

              let baseDate = new Date(`${dayKey}T00:00:00`)

              if (Number.isNaN(baseDate.getTime())) {
                baseDate = !Number.isNaN(parsedDate.getTime())
                  ? parsedDate
                  : new Date()
              }

              const minutesPartition = 1440 / (totalOnDay + 1)
              const minutesOffset = Math.round(minutesPartition * currentIndex)

              let nextDate = add(baseDate, { minutes: minutesOffset })

              const lastEntry = acc.length ? acc[acc.length - 1] : undefined

              if (lastEntry) {
                const lastDate = new Date(lastEntry.date)

                if (nextDate <= lastDate) {
                  nextDate = add(lastDate, { minutes: 1 })
                }
              }

              acc.push(cloneDatum(item, { date: formatISO(nextDate) }))

              return acc
            },
            []
          )

          const isMilestoneRecognition = res.recognitionMethod === 'MILESTONE'

          const recognizedSeries = orderedSeries.map(item =>
            cloneDatum(item, {
              value: item.recognizedValue,
              displayDate: item.displayDate ?? item.date
            })
          )

          const remainingSeries = orderedSeries.map(item =>
            cloneDatum(item, {
              value: item.remainingValue,
              displayDate: item.displayDate ?? item.date
            })
          )

          const fallbackDate = new Date()

          const dateFrom = orderedSeries.length
            ? new Date(orderedSeries[0].date)
            : fallbackDate

          const dateTo = orderedSeries.length
            ? new Date(orderedSeries[orderedSeries.length - 1].date)
            : fallbackDate

          return {
            currency: res.progress.currency,
            recognitionMethod: res.recognitionMethod,
            dateFrom,
            dateTo,
            totalAmount: originalAmount,
            rawPoints: orderedSeries,
            recognitionTerm: {
              start: res.recognitionTerm?.start
                ? new Date(res.recognitionTerm.start)
                : undefined,
              end: res.recognitionTerm?.endInclusive
                ? new Date(res.recognitionTerm.endInclusive)
                : undefined
            },
            series: {
              cumulative: recognizedSeries,
              step: isMilestoneRecognition ? remainingSeries : recognizedSeries,
              cumulativeStep: isMilestoneRecognition
                ? recognizedSeries
                : recognizedSeries
            }
          }
        }
      }
    )

    /**
     * We throw this error to be caught by the appropriate error boundary.
     */
    if (scheduleQuery.error) {
      throw new Error('Deferred revenue chart data could not be loaded')
    }

    if (scheduleQuery.isPending) {
      return {
        isLoading: true,
        chartData: null
      }
    }

    invariant(scheduleQuery.data, 'Chart data should be defined, if loaded')

    return {
      isLoading: false,
      chartData: scheduleQuery.data
    }
  }

export const DefRevScheduleProgressGraph = (props: {
  defRevScheduleId: string
}) => {
  const { isLoading, chartData } = useDefRevScheduleProgressGraph({
    scheduleId: props.defRevScheduleId
  })

  const isMilestoneRecognition = chartData?.recognitionMethod === 'MILESTONE'

  const milestoneEvents = useMemo(() => {
    if (!isMilestoneRecognition || !chartData) {
      return [] as Array<{ point: ChartDatum; delta: number }>
    }

    return chartData.rawPoints.reduce<
      Array<{ point: ChartDatum; delta: number }>
    >((events, point, idx, array) => {
      const previousRecognized = idx === 0 ? 0 : array[idx - 1].recognizedValue
      const delta = point.recognizedValue - previousRecognized

      if (delta > 0.0001) {
        events.push({ point, delta })
      }

      return events
    }, [])
  }, [chartData, isMilestoneRecognition])

  const milestoneVisualization = useMemo(() => {
    if (!isMilestoneRecognition || !chartData || isLoading) {
      return { type: 'chart' as const }
    }

    if (milestoneEvents.length === 0) {
      return { type: 'empty' as const }
    }

    if (milestoneEvents.length === 1) {
      return { type: 'single' as const, event: milestoneEvents[0] }
    }

    return { type: 'chart' as const }
  }, [chartData, isMilestoneRecognition, isLoading, milestoneEvents])

  const chartSeries = useMemo<ChartDatum[]>(() => {
    if (!chartData) {
      return []
    }

    if (isMilestoneRecognition) {
      if (milestoneVisualization.type !== 'chart') {
        return []
      }

      return chartData.series.cumulativeStep
    }

    return chartData.series.cumulative
  }, [chartData, isMilestoneRecognition, milestoneVisualization.type])

  const chartDateRange = useMemo(() => {
    if (!chartData) {
      const today = format(new Date(), 'yyyy-MM-dd')
      return { from: today, to: today }
    }

    const from =
      chartSeries.length > 0
        ? chartSeries[0].date
        : format(chartData.dateFrom, 'yyyy-MM-dd')

    const to =
      chartSeries.length > 0
        ? chartSeries[chartSeries.length - 1].date
        : format(chartData.dateTo, 'yyyy-MM-dd')

    return { from, to }
  }, [chartData, chartSeries])

  const yAxisLabels = useMemo(() => {
    if (isLoading || !chartData) {
      return []
    }

    const values = chartSeries.map(d => d.value)
    if (values.length === 0 || Math.max(...values) < chartData.totalAmount) {
      values.push(chartData.totalAmount)
    }

    return calculateYAxisLabels(values)
  }, [chartData, chartSeries, isLoading])

  const singleMilestoneEvent =
    milestoneVisualization.type === 'single'
      ? milestoneVisualization.event
      : null

  const straightLineEmptyState =
    !isMilestoneRecognition && chartSeries.length === 0

  const straightLineStartLabel = chartData?.recognitionTerm?.start
    ? format(chartData.recognitionTerm.start, 'd MMM yyyy')
    : null

  return (
    <Box position="relative">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        marginBottom="8px"
      >
        <Box {...Primary13SemiBold} color={GreyGrey80}>
          Recognition flow
        </Box>
      </Flex>
      {isLoading ? (
        <Skeleton width="100%" height="292px" />
      ) : milestoneVisualization.type === 'empty' ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap="12px"
          height="292px"
          border={`1px dashed ${GreyGrey30}`}
          borderRadius="8px"
        >
          <IconCircleInfo size={24} color={GreyGrey60} />
          <Text {...Primary13Regular} color={GreyGrey70}>
            No revenue recognized yet
          </Text>
        </Flex>
      ) : milestoneVisualization.type === 'single' &&
        singleMilestoneEvent &&
        chartData ? (
        <Box
          border={`1px solid ${GreyGrey30}`}
          borderRadius="8px"
          padding="16px"
          display="flex"
          flexDirection="column"
          gap="12px"
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Box>
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                Milestone recognized
              </Text>
              <Text {...Primary13Regular} color={GreyGrey60}>
                {format(
                  new Date(
                    singleMilestoneEvent.point.displayDate ??
                      singleMilestoneEvent.point.date
                  ),
                  'd MMM yyyy'
                )}
              </Text>
            </Box>
            {Math.abs(
              singleMilestoneEvent.point.recognizedValue - chartData.totalAmount
            ) < 0.005 && (
              <Box
                padding="4px 8px"
                borderRadius="999px"
                backgroundColor={GreyGrey30}
                color={GreyGrey80}
                {...Primary13SemiBold}
              >
                Fully recognized
              </Box>
            )}
          </Flex>
          <Box>
            <Text {...Primary13Regular} color={GreyGrey60} marginBottom="4px">
              Amount
            </Text>
            <Text {...Primary13SemiBold} color={GreyGrey90}>
              {toMoney({
                value: singleMilestoneEvent.delta.toString(),
                currency: chartData.currency
              })}
            </Text>
          </Box>
          {singleMilestoneEvent.point.userEmail && (
            <Flex gap="6px" alignItems="center">
              <IconPeopleCircle color={GreyGrey70} />
              <Text {...Primary13Regular} color={GreyGrey70}>
                {singleMilestoneEvent.point.userEmail}
              </Text>
            </Flex>
          )}
        </Box>
      ) : straightLineEmptyState ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap="12px"
          height="292px"
          border={`1px dashed ${GreyGrey30}`}
          borderRadius="8px"
        >
          <IconCircleInfo size={24} color={GreyGrey60} />
          <Text {...Primary13Regular} color={GreyGrey70} textAlign="center">
            {straightLineStartLabel
              ? `Recognition starts ${straightLineStartLabel}`
              : 'No revenue recognized yet'}
          </Text>
        </Flex>
      ) : (
        <Box width="100%" height="292px">
          <XYChart<ChartDatum>
            variant={isMilestoneRecognition ? 'stepLine' : 'line'}
            dateFrom={chartDateRange.from}
            dateTo={chartDateRange.to}
            data={chartSeries}
            dateFormat="d MMM yyyy"
            unit={toCurrencySymbol(chartData.currency)}
            animate={false}
            unitTickValues={yAxisLabels}
            renderTooltip={item => (
              <GraphDataTooltip
                balances={item.balances}
                userEmail={item.userEmail}
                displayDate={item.displayDate}
                currency={chartData.currency}
                date={new Date(item.date)}
              />
            )}
          />
        </Box>
      )}
    </Box>
  )
}
